import {Children, cloneElement, isValidElement} from 'react'
import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'
import {Label} from '@/components/ui/label'

interface FormFieldProps {
    label: string
    htmlFor: string
    required?: boolean
    error?: string
    fullWidth?: boolean
    children: ReactNode
}

export function FormField({label, htmlFor, required, error, fullWidth, children}: FormFieldProps) {
    // eslint-disable-next-line i18next/no-literal-string -- ID suffix is not a user-visible string
    const errorId = `${htmlFor}-error`

    /* eslint-disable i18next/no-literal-string -- ARIA attribute values are not user-visible strings */
    const enhancedChildren = Children.map(children, (child) => {
        if (!isValidElement(child)) return child
        return cloneElement(child as React.ReactElement<Record<string, unknown>>, {
            'aria-describedby': errorId,
            'aria-required': required ? 'true' : undefined,
            'aria-invalid': error ? 'true' : undefined,
        })
    })
    /* eslint-enable i18next/no-literal-string */

    return (
        <div className={cn('grid gap-2', fullWidth && 'col-span-2')}>
            <Label htmlFor={htmlFor}>
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {enhancedChildren}
            <span
                id={errorId}
                role="alert"
                data-testid={error ? `${htmlFor}-error` : undefined}
                className={cn('text-sm text-destructive', !error && 'sr-only')}
                aria-live="assertive"
            >
                {error ?? ''}
            </span>
        </div>
    )
}
